import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { fmtPrice, fmtUsd } from '../utils/fmtPrice';
import './SingleAssetHlPortfolio.css';

const API_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) ||
  (process.env.REACT_APP_API_URL
    ? `${String(process.env.REACT_APP_API_URL).replace(/\/$/, '')}/api`
    : 'https://trading.omniscalping.com/api');

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('sessionToken')}`,
});

function fmtQty(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  if (n >= 1) return n.toFixed(8).replace(/\.?0+$/, '') || n.toFixed(8);
  return n.toFixed(8);
}

function fmtMarginUsd(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function lotMarginUsd(lot) {
  return lot.marginUsd ?? lot.sizeUsd ?? 0;
}

export default function SingleAssetHlPortfolio({
  open,
  onClose,
  onTradeLot,
  onCloseLot,
  onStatusMessage,
  refreshKey = 0,
}) {
  const [lots, setLots] = useState([]);
  const [walletAddress, setWalletAddress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE}/single-asset-hl/positions`, { headers: authHeaders() });
      setLots(data.lots || []);
      setWalletAddress(data.walletAddress || null);
    } catch (e) {
      onStatusMessage?.(e.response?.data?.error || e.message, true);
      setLots([]);
    } finally {
      setLoading(false);
    }
  }, [onStatusMessage]);

  useEffect(() => {
    if (!open) return;
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [open, load, refreshKey]);

  const handleClose = async (lot) => {
    const margin = lotMarginUsd(lot);
    const lev = lot.leverage || 1;
    if (
      !window.confirm(
        `Close entire ${lot.positionAction} on ${lot.symbol}? (Margin ${fmtMarginUsd(margin)} · ${lev}x · ${fmtQty(lot.sizeContracts)} qty)`
      )
    ) {
      return;
    }
    setBusyKey(lot.lotKey);
    try {
      await onCloseLot?.(lot);
      await load();
    } finally {
      setBusyKey(null);
    }
  };

  if (!open) return null;

  return (
    <div className="sahl-portfolio-overlay" role="presentation" onClick={onClose}>
      <div className="sahl-portfolio-dialog" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <header>
          <h2>Portfolio — Single Asset HL</h2>
          <button type="button" className="sahl-portfolio-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>
        <p className="sahl-portfolio-hint">
          Open lots from this page only — separate wallet from Multi-Asset manual/oBot.{' '}
          {walletAddress ? (
            <code>
              {walletAddress.slice(0, 6)}…{walletAddress.slice(-4)}
            </code>
          ) : (
            'Link wallet via gear icon.'
          )}
        </p>
        {loading && <p className="sahl-muted">Loading…</p>}
        {!loading && !lots.length && <p className="sahl-muted">No open Single Asset HL positions.</p>}
        <ul className="sahl-portfolio-list">
          {lots.map((lot) => {
            const busy = busyKey === lot.lotKey;
            return (
              <li key={lot.lotKey}>
                <div className="sahl-portfolio-row">
                  <div className="sahl-portfolio-row-top">
                    <strong className="sahl-portfolio-symbol">{lot.symbol}</strong>
                    <span className={`sahl-action-pill ${lot.side}`}>{lot.positionAction}</span>
                    <span className={`sahl-mode-pill-sm ${lot.isPaper ? 'paper' : 'live'}`}>
                      {lot.isPaper ? 'Paper' : 'Live'}
                    </span>
                  </div>
                  <div className="sahl-portfolio-stats">
                    <span>
                      Size <strong>{fmtMarginUsd(lotMarginUsd(lot))}</strong>
                    </span>
                    <span>
                      Leverage <strong>{lot.leverage || 1}x</strong>
                    </span>
                    <span>
                      Qty <strong>{fmtQty(lot.sizeContracts)}</strong>
                    </span>
                    <span>
                      Price <strong>{fmtPrice(lot.entryPrice)}</strong>
                    </span>
                    {lot.unrealizedPnl != null && (
                      <span className={lot.unrealizedPnl >= 0 ? 'pnl-up' : 'pnl-down'}>
                        uPnL <strong>{fmtUsd(lot.unrealizedPnl)}</strong>
                      </span>
                    )}
                  </div>
                </div>
                <div className="sahl-portfolio-row-actions">
                  <button
                    type="button"
                    className="sahl-trade-btn"
                    disabled={busy}
                    onClick={() => onTradeLot?.(lot)}
                    title={
                      lot.side === 'long'
                        ? 'Partial sell — reduce this long lot'
                        : 'Partial buy — reduce this short lot'
                    }
                  >
                    Trade
                  </button>
                  <button
                    type="button"
                    className="sahl-close-btn"
                    disabled={busy}
                    onClick={() => handleClose(lot)}
                    title={
                      lot.side === 'long'
                        ? 'Sell entire lot (full close)'
                        : 'Buy entire lot (full close)'
                    }
                  >
                    {busy ? '…' : 'Close'}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
        <footer>
          <button type="button" className="secondary-btn" disabled={!!busyKey} onClick={load}>
            Refresh
          </button>
        </footer>
      </div>
    </div>
  );
}
